import {
  AlertCircle,
  Building,
  Calendar,
  Clock,
  FileText,
  Globe,
  MapPin,
  Plus,
  Save,
  Tag,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import DatePicker from '../forms/DatePicker';
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { useMeetingDrawer } from '../../hooks/useMeetingDrawer';
import { useToast } from '../../contexts/ToastContext';
import meetingService from '../../services/meetingService';
import {
  CreateMeetingData,
  MeetingRecurrence,
  MeetingVisibility,
  UpdateMeetingData,
} from '../../types';
import { formatDateTimeForInput } from '../../utils/dateUtils';
import ImprovedCategorySelector from './ImprovedCategorySelector';
import UnifiedUploader, { UnifiedUploadItem } from '../forms/UnifiedUploader';
import ProjectMemberSelector from './ProjectMemberSelector';
import ProjectSelector from './ProjectSelector';
import RecurrenceSelector from './RecurrenceSelector';

interface FormData extends CreateMeetingData {
  selectedProjectId?: string;
  externalUsers: Array<{
    email: string;
    name?: string;
    type?: string;
    status?: string;
    respondedAt?: Date;
  }>;
  tags: string[];
}

const RootMeetingDrawer: React.FC = () => {
  const { drawerState, closeMeetingDrawer } = useMeetingDrawer();
  const { success, error } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [hasApiErrors, setHasApiErrors] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<UnifiedUploadItem[]>([]);

  // Form state
  const [formData, setFormData] = useState<FormData>({
    title: '',
    categoryId: '',
    selectedProjectId: '',
    tags: [],
    details: '',
    location: '',
    startDateTime: '',
    endDateTime: '',
    scheduleType: 'one_time',
    recurrence: undefined,
    visibility: 'private',
    attachments: [],
    projectMembers: [],
    externalUsers: [],
    reminders: [
      { type: 'both', minutesBefore: 15, isActive: true },
      { type: 'email', minutesBefore: 1440, isActive: true },
    ],
  });

  // Initialize form data
  useEffect(() => {
    if (drawerState.meeting && drawerState.mode !== 'create') {
      const meeting = drawerState.meeting;

      // Convert existing attachments to upload items for display
      if (meeting.attachments && meeting.attachments.length > 0) {
        const existingFiles = meeting.attachments.map((att: any, index: number) => ({
          id: `existing-${index}`,
          file: new File([], att.originalName || att.filename),
          status: 'completed' as const,
          progress: 100,
          referenceId: att.path,
        }));
        setUploadFiles(existingFiles);
      } else {
        setUploadFiles([]);
      }

      setFormData({
        title: meeting.title || '',
        categoryId: meeting.categoryId || undefined,
        selectedProjectId: meeting.projectId || drawerState.projectId || undefined,
        tags: meeting.tags || [],
        details: meeting.details || '',
        location: meeting.location || '',
        startDateTime: meeting.startDateTime
          ? formatDateTimeForInput(new Date(meeting.startDateTime))
          : '',
        endDateTime: meeting.endDateTime
          ? formatDateTimeForInput(new Date(meeting.endDateTime))
          : '',
        scheduleType: meeting.scheduleType || 'one_time',
        recurrence: meeting.recurrence,
        visibility: meeting.visibility || 'private',
        attachments: meeting.attachments || [],
        projectMembers: meeting.projectMembers || [],
        externalUsers:
          meeting.externalUsers?.map(user => ({
            email: user.email,
            name: user.name || '',
            type: (user as any).type || 'external',
            status: typeof user.status === 'string' ? user.status : 'pending',
            respondedAt: user.respondedAt instanceof Date ? user.respondedAt : undefined,
          })) || [],
        reminders: meeting.reminders || [
          { type: 'both', minutesBefore: 15, isActive: true },
          { type: 'email', minutesBefore: 1440, isActive: true },
        ],
      });
    } else {
      // Reset form for create mode and properly set the project ID
      // Use default times if provided (from calendar click)
      const defaultStartDateTime = drawerState.defaultStartTime
        ? formatDateTimeForInput(drawerState.defaultStartTime)
        : '';
      const defaultEndDateTime = drawerState.defaultEndTime
        ? formatDateTimeForInput(drawerState.defaultEndTime)
        : '';

      // Reset upload files for new meeting
      setUploadFiles([]);

      setFormData({
        title: '',
        categoryId: '',
        selectedProjectId: drawerState.projectId || '',
        tags: [],
        details: '',
        location: '',
        startDateTime: defaultStartDateTime,
        endDateTime: defaultEndDateTime,
        scheduleType: 'one_time',
        recurrence: undefined,
        visibility: 'private',
        attachments: [],
        projectMembers: [],
        externalUsers: [],
        reminders: [
          { type: 'both', minutesBefore: 15, isActive: true },
          { type: 'email', minutesBefore: 1440, isActive: true },
        ],
      });
    }
    setErrors({});
    setHasApiErrors(false);
  }, [
    drawerState.meeting,
    drawerState.mode,
    drawerState.projectId,
    drawerState.defaultStartTime,
    drawerState.defaultEndTime,
  ]);

  // Validation
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Meeting title is required';
    }

    if (!formData.selectedProjectId && !drawerState.projectId) {
      newErrors.selectedProjectId = 'Project selection is required';
    }

    if (!formData.startDateTime) {
      newErrors.startDateTime = 'Start date and time is required';
    }

    if (!formData.endDateTime) {
      newErrors.endDateTime = 'End date and time is required';
    }

    if (formData.startDateTime && formData.endDateTime) {
      const start = new Date(formData.startDateTime);
      const end = new Date(formData.endDateTime);
      if (end <= start) {
        newErrors.endDateTime = 'End time must be after start time';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission with improved error handling
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setHasApiErrors(false);

    try {
      const targetProjectId = drawerState.projectId || formData.selectedProjectId;

      if (drawerState.mode === 'create') {
        const cleanedFormData = {
          ...formData,
          categoryId:
            formData.categoryId && formData.categoryId.length >= 24
              ? formData.categoryId
              : undefined,
          tags: formData.tags || [],
          details: formData.details?.trim() || undefined,
          location: formData.location?.trim() || undefined,
          projectMembers: formData.projectMembers || [],
          externalUsers: formData.externalUsers || [],
          reminders: formData.reminders || [],
        };

        const result = await meetingService.createMeeting(cleanedFormData, targetProjectId);
        if (result.success) {
          success('Meeting created successfully');
          if (drawerState.onSave) {
            drawerState.onSave();
          }
          closeMeetingDrawer();
        } else {
          setHasApiErrors(true);
          error(result.message || 'Failed to create meeting');
          if (result.errors) {
            console.error('Validation errors:', result.errors);
          }
        }
      } else if (drawerState.mode === 'edit' && drawerState.meeting) {
        const updateData: UpdateMeetingData = {
          ...formData,
          categoryId:
            formData.categoryId && formData.categoryId.length >= 24
              ? formData.categoryId
              : undefined,
          tags: formData.tags || [],
          details: formData.details?.trim() || undefined,
          location: formData.location?.trim() || undefined,
          projectMembers: formData.projectMembers || [],
          externalUsers: formData.externalUsers || [],
          reminders: formData.reminders || [],
        };
        const result = await meetingService.updateMeeting(
          targetProjectId || '',
          drawerState.meeting._id || '',
          updateData
        );
        if (result.success) {
          success('Meeting updated successfully');
          if (drawerState.onSave) {
            drawerState.onSave();
          }
          closeMeetingDrawer();
        } else {
          setHasApiErrors(true);
          error(result.message || 'Failed to update meeting');
          if (result.errors) {
            console.error('Validation errors:', result.errors);
          }
        }
      }
    } catch (err) {
      console.error('Error saving meeting:', err);
      setHasApiErrors(true);
      error('Network error: Please check your connection and try again');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle delete with improved error handling
  const handleDelete = async () => {
    if (!drawerState.meeting) return;

    const confirmed = window.confirm('Are you sure you want to delete this meeting?');
    if (!confirmed) return;

    setIsLoading(true);
    setHasApiErrors(false);

    try {
      const targetProjectId = drawerState.projectId || formData.selectedProjectId;
      const result = await meetingService.deleteMeeting(
        targetProjectId || '',
        drawerState.meeting._id || ''
      );
      if (result.success) {
        success('Meeting deleted successfully');
        if (drawerState.onDelete) {
          drawerState.onDelete();
        }
        closeMeetingDrawer();
      } else {
        setHasApiErrors(true);
        error(result.message || 'Failed to delete meeting');
      }
    } catch (err) {
      console.error('Error deleting meeting:', err);
      setHasApiErrors(true);
      error('Network error: Please check your connection and try again');
    } finally {
      setIsLoading(false);
    }
  };

  // External user management
  const addExternalUser = () => {
    setFormData(prev => ({
      ...prev,
      externalUsers: [
        ...(prev.externalUsers || []),
        { email: '', name: '', type: 'external', status: 'pending' },
      ],
    }));
  };

  const updateExternalUser = (index: number, field: 'email' | 'name' | 'type', value: string) => {
    setFormData(prev => ({
      ...prev,
      externalUsers: (prev.externalUsers || []).map((user, i) =>
        i === index ? { ...user, [field]: value } : user
      ),
    }));
  };

  const removeExternalUser = (index: number) => {
    setFormData(prev => ({
      ...prev,
      externalUsers: (prev.externalUsers || []).filter((_, i) => i !== index),
    }));
  };

  // Tag management
  const addTag = (tag: string) => {
    if (tag.trim() && !(formData.tags || []).includes(tag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...(prev.tags || []), tag.trim()],
      }));
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: (prev.tags || []).filter(tag => tag !== tagToRemove),
    }));
  };

  const handleTagKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const input = e.target as HTMLInputElement;
      addTag(input.value);
      input.value = '';
    }
  };

  // Don't render if not open
  if (!drawerState.isOpen) return null;

  // Portal the drawer to document.body for proper z-index layering
  const drawerContent = (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90] animate-in fade-in duration-300"
        onClick={closeMeetingDrawer}
      />

      {/* Drawer */}
      <div className="fixed top-4 right-4 bottom-4 z-[100] w-[680px] animate-in slide-in-from-right duration-300">
        <div className="bg-white rounded-xl shadow-2xl border border-gray-200 h-full flex flex-col overflow-hidden">
          {/* Header */}
          <div className="flex-shrink-0 flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white rounded-t-xl">
            <div className="flex items-center space-x-3">
              <div className="bg-brand-primary/10 p-2 rounded-lg">
                <Calendar className="h-5 w-5 text-brand-primary" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">
                  {drawerState.mode === 'create'
                    ? 'New Meeting'
                    : drawerState.mode === 'edit'
                      ? 'Edit Meeting'
                      : 'Meeting Details'}
                </h2>
                <p className="text-sm text-gray-500">
                  {drawerState.mode === 'create'
                    ? 'Schedule and organize your meeting'
                    : drawerState.mode === 'edit'
                      ? 'Update meeting information'
                      : 'View meeting details'}
                </p>
              </div>
            </div>
            <button
              onClick={closeMeetingDrawer}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* API Error Banner */}
          {hasApiErrors && (
            <div className="bg-red-50 border border-red-200 p-4 flex items-center space-x-3">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm text-red-700">
                  Some components may not load properly due to network issues. You can still create
                  the meeting with available information.
                </p>
              </div>
            </div>
          )}

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            <form id="meeting-form" onSubmit={handleSubmit} className="brand-form p-6 space-y-6">
              {/* Project Selection - Only show for global meetings (no project context) */}
              {!drawerState.projectId && (
                <div>
                  <ProjectSelector
                    value={formData.selectedProjectId || ''}
                    onChange={selectedProjectId =>
                      setFormData(prev => ({ ...prev, selectedProjectId }))
                    }
                    disabled={drawerState.mode === 'view'}
                    required={true}
                    label="Associated Project"
                    placeholder="Select the project for this meeting..."
                  />
                  {errors.selectedProjectId && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {errors.selectedProjectId}
                    </p>
                  )}
                </div>
              )}

              {/* Project Context Display - Show when in project context */}
              {drawerState.projectId && (
                <div className="bg-brand-50 border border-brand-200 rounded-lg p-3">
                  <div className="flex items-center space-x-2">
                    <Building className="w-4 h-4 text-brand-primary" />
                    <span className="text-sm font-medium text-brand-900">Project Meeting</span>
                  </div>
                  <p className="text-xs text-brand-700 mt-1">
                    This meeting will be associated with the current project
                  </p>
                </div>
              )}

              {/* Basic Information Row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Title */}
                <div className="lg:col-span-2">
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-2">
                    Meeting Title <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="title"
                    value={formData.title}
                    onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    disabled={drawerState.mode === 'view'}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary sm:text-sm disabled:bg-gray-50 transition-colors"
                    placeholder="Enter a clear, descriptive meeting title"
                  />
                  {errors.title && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {errors.title}
                    </p>
                  )}
                </div>

                {/* Category */}
                <div>
                  <ImprovedCategorySelector
                    value={formData.categoryId || ''}
                    onChange={categoryId => setFormData(prev => ({ ...prev, categoryId }))}
                    disabled={drawerState.mode === 'view'}
                    required={false}
                  />
                </div>

                {/* Location */}
                <div>
                  <label
                    htmlFor="location"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    <MapPin className="h-4 w-4 inline mr-1" />
                    Location
                  </label>
                  <input
                    type="text"
                    id="location"
                    value={formData.location}
                    onChange={e => setFormData(prev => ({ ...prev, location: e.target.value }))}
                    disabled={drawerState.mode === 'view'}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary sm:text-sm disabled:bg-gray-50 transition-colors"
                    placeholder="Meeting room, address, or video link"
                  />
                </div>
              </div>

              {/* Schedule & Details Row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Start Date & Time */}
                <div>
                  <DatePicker
                    label="Start Date & Time"
                    required={true}
                    showTimePicker={true}
                    value={typeof formData.startDateTime === 'string' ? formData.startDateTime : null}
                    onChange={date => setFormData(prev => ({ ...prev, startDateTime: date ?? '' }))}
                    disabled={drawerState.mode === 'view'}
                    error={errors.startDateTime}
                  />
                </div>

                {/* End Date & Time */}
                <div>
                  <DatePicker
                    label="End Date & Time"
                    required={true}
                    showTimePicker={true}
                    value={typeof formData.endDateTime === 'string' ? formData.endDateTime : null}
                    onChange={date => setFormData(prev => ({ ...prev, endDateTime: date ?? '' }))}
                    disabled={drawerState.mode === 'view'}
                    error={errors.endDateTime}
                  />
                </div>
              </div>

              {/* Details & Agenda */}
              <div>
                <label htmlFor="details" className="block text-sm font-medium text-gray-700 mb-2">
                  <FileText className="h-4 w-4 inline mr-1" />
                  Details & Agenda
                </label>
                <textarea
                  id="details"
                  rows={3}
                  value={formData.details}
                  onChange={e => setFormData(prev => ({ ...prev, details: e.target.value }))}
                  disabled={drawerState.mode === 'view'}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary sm:text-sm disabled:bg-gray-50 transition-colors"
                  placeholder="Enter meeting details, agenda, or notes"
                />
              </div>

              {/* Optional Settings Row */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Meeting Type */}
                <div>
                  <label
                    htmlFor="scheduleType"
                    className="block text-sm font-medium text-gray-700 mb-2"
                  >
                    Meeting Type
                  </label>
                  <select
                    id="scheduleType"
                    value={formData.scheduleType}
                    onChange={e =>
                      setFormData(prev => ({ ...prev, scheduleType: e.target.value as any }))
                    }
                    disabled={drawerState.mode === 'view'}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary sm:text-sm disabled:bg-gray-50 transition-colors"
                  >
                    <option value="one_time">One-time Meeting</option>
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>

                {/* Visibility - Only show for project-specific meetings */}
                {(drawerState.projectId || formData.selectedProjectId) && (
                  <div className="lg:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <Globe className="h-4 w-4 inline mr-1" />
                      Visibility
                    </label>
                    <div className="flex space-x-4">
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="visibility"
                          value="private"
                          checked={formData.visibility === 'private'}
                          onChange={e =>
                            setFormData(prev => ({
                              ...prev,
                              visibility: e.target.value as MeetingVisibility,
                            }))
                          }
                          disabled={drawerState.mode === 'view'}
                          className="h-4 w-4 text-brand-primary focus:ring-brand-primary border-gray-300"
                        />
                        <span className="ml-2 text-sm text-gray-700">Private</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="radio"
                          name="visibility"
                          value="project_wide"
                          checked={formData.visibility === 'project_wide'}
                          onChange={e =>
                            setFormData(prev => ({
                              ...prev,
                              visibility: e.target.value as MeetingVisibility,
                            }))
                          }
                          disabled={drawerState.mode === 'view'}
                          className="h-4 w-4 text-brand-primary focus:ring-brand-primary border-gray-300"
                        />
                        <span className="ml-2 text-sm text-gray-700">Project-wide</span>
                      </label>
                    </div>
                  </div>
                )}
              </div>

              {/* Recurrence Settings */}
              {formData.scheduleType !== 'one_time' && (
                <RecurrenceSelector
                  value={formData.recurrence as MeetingRecurrence}
                  onChange={recurrence => setFormData(prev => ({ ...prev, recurrence }))}
                  scheduleType={formData.scheduleType}
                  disabled={drawerState.mode === 'view'}
                />
              )}

              {/* Attachments */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Attachments</label>
                <UnifiedUploader
                  files={uploadFiles}
                  onChange={setUploadFiles}
                  onUploadComplete={item => {
                    // Convert uploaded item to attachment format
                    const attachment = {
                      filename: item.file.name,
                      originalName: item.file.name,
                      size: item.file.size,
                      mimetype: item.file.type,
                      path: item.referenceId || '',
                      uploadedAt: new Date(),
                    };
                    setFormData(prev => ({
                      ...prev,
                      attachments: [...(prev.attachments || []), attachment],
                    }));
                  }}
                  disabled={drawerState.mode === 'view'}
                  multiple={true}
                  maxFiles={5}
                  maxSize={50}
                  module="meetings"
                  projectId={drawerState.projectId}
                  autoUpload={true}
                  showProgress={true}
                />
              </div>

              {/* Project Members - Show when project is selected */}
              {(drawerState.projectId || formData.selectedProjectId) && (
                <div>
                  <ProjectMemberSelector
                    projectId={drawerState.projectId || formData.selectedProjectId || ''}
                    selectedMembers={formData.projectMembers || []}
                    onChange={memberIds =>
                      setFormData(prev => ({ ...prev, projectMembers: memberIds }))
                    }
                    disabled={drawerState.mode === 'view'}
                    placeholder="Select project members to invite to this meeting"
                    label="Meeting Invitees"
                  />
                </div>
              )}

              {/* External Participants - Simplified */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <label className="block text-sm font-medium text-gray-700">
                    <Users className="h-4 w-4 inline mr-1" />
                    External Participants
                  </label>
                  {drawerState.mode !== 'view' && (
                    <button
                      type="button"
                      onClick={addExternalUser}
                      className="inline-flex items-center px-3 py-1 text-xs font-medium text-brand-primary bg-brand-50 hover:bg-brand-100 rounded-md transition-colors"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add
                    </button>
                  )}
                </div>

                {(formData.externalUsers || []).length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-2">No external participants</p>
                ) : (
                  <div className="space-y-2">
                    {(formData.externalUsers || []).map((user, index) => (
                      <div
                        key={index}
                        className="flex space-x-2 p-2 bg-gray-50 rounded-lg border border-gray-200"
                      >
                        <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-2">
                          <input
                            type="email"
                            value={user.email}
                            onChange={e => updateExternalUser(index, 'email', e.target.value)}
                            disabled={drawerState.mode === 'view'}
                            className="block w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-brand-primary disabled:bg-gray-50"
                            placeholder="Email address"
                          />
                          <input
                            type="text"
                            value={user.name || ''}
                            onChange={e => updateExternalUser(index, 'name', e.target.value)}
                            disabled={drawerState.mode === 'view'}
                            className="block w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-brand-primary disabled:bg-gray-50"
                            placeholder="Name (optional)"
                          />
                        </div>
                        {drawerState.mode !== 'view' && (
                          <button
                            type="button"
                            onClick={() => removeExternalUser(index)}
                            className="text-red-600 hover:text-red-700 p-1"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Tags - Simplified */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Tag className="h-4 w-4 inline mr-1" />
                  Tags
                </label>

                {(formData.tags || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {(formData.tags || []).map((tag, index) => (
                      <span
                        key={index}
                        className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-brand-100 text-brand-800"
                      >
                        {tag}
                        {drawerState.mode !== 'view' && (
                          <button
                            type="button"
                            onClick={() => removeTag(tag)}
                            className="ml-1 text-brand-600 hover:text-brand-700"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        )}
                      </span>
                    ))}
                  </div>
                )}

                {drawerState.mode !== 'view' && (
                  <input
                    type="text"
                    onKeyPress={handleTagKeyPress}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-primary"
                    placeholder="Type a tag and press Enter"
                  />
                )}
              </div>
            </form>
          </div>

          {/* Footer */}
          <div className="flex-shrink-0 border-t border-gray-200 px-6 py-4 bg-gray-50 rounded-b-xl">
            <div className="flex items-center justify-between">
              <div>
                {drawerState.mode === 'edit' && (
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={isLoading}
                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-red-600 bg-white border border-red-300 rounded-lg hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 transition-colors"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </button>
                )}
              </div>

              <div className="flex items-center space-x-3">
                <button
                  type="button"
                  onClick={closeMeetingDrawer}
                  className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>

                {drawerState.mode !== 'view' && (
                  <button
                    type="submit"
                    form="meeting-form"
                    disabled={isLoading}
                    className="px-6 py-2 text-sm font-medium text-white bg-brand-primary border border-transparent rounded-lg hover:bg-brand-600 focus:outline-none focus:ring-2 focus:ring-brand-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  >
                    {isLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        {drawerState.mode === 'create' ? 'Creating...' : 'Updating...'}
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        {drawerState.mode === 'create' ? 'Create Meeting' : 'Update Meeting'}
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  // Use portal to render at document.body level
  return ReactDOM.createPortal(drawerContent, document.body);
};

export default RootMeetingDrawer;
